import { lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import LegacyRedirect from './components/LegacyRedirect';
import { legacyAccountingPaths } from './nav';
import { sections } from './sections';
import type { SectionDef } from './pages/AccountingSectionPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const AccountingOverviewPage = lazy(() => import('./pages/AccountingHomePage'));
const AccountingSectionPage = lazy(() => import('./pages/AccountingSectionPage'));

/**
 * The accounting module's routes, spread into the signed-in layout. Every page sits behind
 * `RequireModule`, so an organisation without the module sees the "not enabled" card at these URLs
 * rather than a page that would only meet 403s. The URL mirrors the menu: the ledger, then the
 * section, then the leaf (`/accounting/general-ledger/transactions/journal`).
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Accounting.View}>
    <RequireModule module={Modules.Accounting}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: SectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<AccountingSectionPage section={def as unknown as SectionDef} />) });

export const accountingRoutes: RouteObject[] = [
  { path: 'accounting', element: guard(<AccountingOverviewPage />) },

  // 1 General Ledger
  { path: 'accounting/general-ledger', element: <Navigate to="/accounting/general-ledger/chart-of-accounts" replace /> },
  section(sections.glChart, 'accounting/general-ledger/chart-of-accounts'),
  section(sections.glTransactions, 'accounting/general-ledger/transactions/:view?'),
  section(sections.glCashBook, 'accounting/general-ledger/cash-book/:view?'),
  section(sections.glReports, 'accounting/general-ledger/reports/:view?'),
  section(sections.budgets, 'accounting/general-ledger/budgets'),
  section(sections.glClose, 'accounting/general-ledger/close'),

  // 2 Sales Ledger
  { path: 'accounting/sales-ledger', element: <Navigate to="/accounting/sales-ledger/customers" replace /> },
  section(sections.salesCustomers, 'accounting/sales-ledger/customers'),
  section(sections.salesTransactions, 'accounting/sales-ledger/transactions/:view?'),
  section(sections.salesReports, 'accounting/sales-ledger/reports/:view?'),
  section(sections.salesCreditControl, 'accounting/sales-ledger/credit-control/:view?'),
  section(sections.salesClose, 'accounting/sales-ledger/close'),

  // 3 Purchase Ledger (the money document is a payment; "receipt" is honoured as a spelling of it)
  { path: 'accounting/purchase-ledger', element: <Navigate to="/accounting/purchase-ledger/suppliers" replace /> },
  section(sections.purchaseSuppliers, 'accounting/purchase-ledger/suppliers/:view?'),
  section(sections.purchaseRequisitions, 'accounting/purchase-ledger/requisitions'),
  section(sections.purchaseOrders, 'accounting/purchase-ledger/purchase-orders/:view?'),
  section(sections.purchaseMatchExceptions, 'accounting/purchase-ledger/match-exceptions'),
  { path: 'accounting/purchase-ledger/transactions/receipt', element: <LegacyRedirect to="/accounting/purchase-ledger/transactions/payment" /> },
  section(sections.purchaseTransactions, 'accounting/purchase-ledger/transactions/:view?'),
  section(sections.purchaseReports, 'accounting/purchase-ledger/reports/:view?'),
  section(sections.purchasePaymentRuns, 'accounting/purchase-ledger/payment-runs'),
  section(sections.purchaseClose, 'accounting/purchase-ledger/close'),

  // Fixed assets
  { path: 'accounting/fixed-assets', element: <Navigate to="/accounting/fixed-assets/register" replace /> },
  section(sections.fixedAssetsRegister, 'accounting/fixed-assets/register/:view?'),
  section(sections.fixedAssetsEvents, 'accounting/fixed-assets/events'),
  section(sections.fixedAssetsDepreciation, 'accounting/fixed-assets/depreciation'),
  section(sections.fixedAssetsVerification, 'accounting/fixed-assets/verification'),
  section(sections.fixedAssetsCapex, 'accounting/fixed-assets/capital-projects/:view?'),

  // The former Budget and Tax menus. Budgets are a general-ledger page, budget types and tax codes are
  // configuration, and the three tax returns are general-ledger reports; every old address still resolves,
  // carrying its query string, so stored notification links and bookmarks land on the right page.
  { path: 'accounting/budget', element: <Navigate to="/accounting/general-ledger/budgets" replace /> },
  { path: 'accounting/budget/budgets', element: <LegacyRedirect to="/accounting/general-ledger/budgets" /> },
  { path: 'accounting/budget/types', element: <Navigate to="/budgeting/settings/profile" replace /> },
  { path: 'accounting/tax', element: <Navigate to="/accounting/settings/tax-codes" replace /> },
  { path: 'accounting/tax/codes', element: <LegacyRedirect to="/accounting/settings/tax-codes" /> },
  { path: 'accounting/tax/vat-returns', element: <LegacyRedirect to="/accounting/general-ledger/reports/vat-returns" /> },
  { path: 'accounting/tax/wht-register', element: <LegacyRedirect to="/accounting/general-ledger/reports/wht-register" /> },
  { path: 'accounting/tax/provisional-income-tax', element: <LegacyRedirect to="/accounting/general-ledger/reports/provisional-income-tax" /> },

  // Settings
  // The settings section carries two tabs (settings, configuration history), so its route takes the view
  // segment like every other tabbed section; the static sibling paths below still win over it.
  section(sections.settings, 'accounting/settings/:view?'),
  section(sections.costCentres, 'accounting/settings/cost-centres'),
  section(sections.fiscalCalendar, 'accounting/settings/fiscal-calendar'),
  section(sections.taxCodes, 'accounting/settings/tax-codes'),
  { path: 'accounting/settings/budget-types', element: <Navigate to="/budgeting/settings/profile" replace /> },
  section(sections.openingBalances, 'accounting/settings/opening-balances'),
  section(sections.closeChecklist, 'accounting/settings/close-checklist'),
  section(sections.controls, 'accounting/settings/controls/:view?'),

  // The former flat tabs, each sent where it now lives.
  ...Object.entries(legacyAccountingPaths)
    .filter(([key]) => key !== 'overview' && key !== 'settings')
    .map(([key, to]) => ({ path: `accounting/${key}`, element: <LegacyRedirect to={to} /> }) satisfies RouteObject),
];
